import React, { useContext, useEffect, useState } from 'react'
import { DashboardContext } from '@/context/DashboardContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { ProjectSmallIcon } from '../../icons/Project'
import toast from 'react-hot-toast'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ChevronDown, ChevronUp, Apple, Tablet, Smartphone, Globe, Laptop, Computer, MonitorSmartphone, Check } from 'lucide-react'

interface ChooseProjectProps {
  meeting: any;
  variant?: 'default' | 'minimal';
}

export function ChooseProject({ meeting, variant = 'default' }: ChooseProjectProps) {
  const { allProjects, changeProject, products, toggleMeetingRecording } = useContext(DashboardContext)
  const [isLoading, setIsLoading] = useState(false)
  const [isRecording, setIsRecording] = useState(meeting.should_record !== false)
  const [expandedProducts, setExpandedProducts] = useState(new Set())
  const customFetch = useCustomFetch()

  useEffect(() => {
    if (meeting.project_id) {
      const project = allProjects.find((project) => project.id === meeting.project_id)
      if (project) meeting.project_name = project.name
    }
  }, [allProjects])

  const getPlatformIcon = (platform: string, size = 16, className = 'text-gray-700') => {
    switch (platform) {
      case 'iOS':
        return <Apple size={size} className={className} strokeWidth={2} />
      case 'iPad OS':
        return <Tablet size={size} className={className} strokeWidth={2} />
      case 'Android':
        return <Smartphone size={size} className={className} strokeWidth={2} />
      case 'Web':
        return <Globe size={size} className={className} strokeWidth={2} />
      case 'Mac OS':
        return <Laptop size={size} className={className} strokeWidth={2} />
      case 'Windows':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Linux':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Multi-Platform':
        return <MonitorSmartphone size={size} className={className} strokeWidth={2} />
      default:
        return <Globe size={size} className={className} strokeWidth={2} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'text-[#B2C920]'
      case 'Beta':
        return 'text-[#964EB2]'
      case 'Alpha':
        return 'text-[#CF3F75]'
      case 'Planning':
        return 'text-[#2C8FB1]'
      case 'Ideation':
        return 'text-[#DCAE1D]'
      default:
        return 'text-gray-500'
    }
  }

  async function handleAssignProject(newProjectId) {
    setIsLoading(true)
    const res = await customFetch('/assignProjectToMeeting', 'PUT', {
      projectId: newProjectId,
      meetingId: meeting.id,
    })
    if (res.error) {
      console.error(res.error)
      toast.error(res.error.msg || 'There was an error assigning the new project')
    } else {
      await changeProject(meeting.id, meeting.project_id, newProjectId)
      toast.success('Project assigned successfully')
    }
    setIsLoading(false)
  }

  async function handleToggleRecording(checked: boolean) {
    setIsLoading(true)
    try {
      await toggleMeetingRecording(meeting.id, checked)
      setIsRecording(checked)
    } catch (err) {
      console.error('error toggling meeting recording', err)
      toast.error('There was an error updating recording settings')
      setIsRecording(!checked)
    }
    setIsLoading(false)
  }

  const toggleProduct = (productId: string) => {
    const newExpandedProducts = new Set()
    if (!expandedProducts.has(productId)) {
      newExpandedProducts.add(productId)
    }
    setExpandedProducts(newExpandedProducts)
  }

  const triggerClassName = variant === 'minimal' 
    ? "flex items-center gap-1 text-[#62708d] text-[13px] font-medium font-['Manrope'] leading-none" 
    : "py-[3px] pl-[6px] pr-[10px] shadow rounded-[30px] border border-[#E4E4E4] flex justify-start items-center gap-[6px] w-full"

  const projectNameClassName = variant === 'minimal'
    ? "text-[#62708d] text-[13px] font-medium font-['Manrope'] leading-none"
    : "text-[#62708d] text-[10px] font-bold font-['Manrope'] leading-3"

  const containerClassName = variant === 'minimal'
    ? "relative flex items-center cursor-pointer"
    : "relative ml-[9px] flex items-center gap-[9px] cursor-pointer max-w-32"

  return (
    <div className={containerClassName} onClick={(e) => e.stopPropagation()}>
      <DropdownMenu onOpenChange={(open) => {
        if (!open) {
          setExpandedProducts(new Set())
        }
      }}>
        <DropdownMenuTrigger asChild>
          <div className={triggerClassName}>
            {!meeting.project_id ? (
              <>
                {variant !== 'minimal' && <ProjectSmallIcon disabled />}
                {meeting.source === 'gong' ? (
                  <p className={projectNameClassName}>Unassigned</p>
                ) : (
                  <p className={projectNameClassName}>Assign</p>
                )}
              </>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <ProjectSmallIcon />
                      <p className={projectNameClassName}>
                        {truncateString(meeting.project_name, variant === 'minimal' ? 8 : 10)}
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{meeting.project_name}</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            <svg width="10" height="6" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.70711 6.79289C6.31658 7.18342 5.68342 7.18342 5.29289 6.79289L0.707105 2.2071C0.0771403 1.57714 0.523309 0.5 1.41421 0.5L10.5858 0.499999C11.4767 0.499999 11.9229 1.57714 11.2929 2.20711L6.70711 6.79289Z"
                fill="#62718D"
              />
            </svg>
            {isLoading && (
              <svg className="w-8 h-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                <radialGradient id="a1" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
                  <stop offset="0" stopColor="#FF156D"></stop>
                  <stop offset=".3" stopColor="#FF156D" stopOpacity=".9"></stop>
                  <stop offset=".6" stopColor="#FF156D" stopOpacity=".6"></stop>
                  <stop offset=".8" stopColor="#FF156D" stopOpacity=".3"></stop>
                  <stop offset="1" stopColor="#FF156D" stopOpacity="0"></stop>
                </radialGradient>
                <circle
                  transform-origin="center"
                  fill="none"
                  stroke="url(#a1)"
                  strokeWidth="15"
                  strokeLinecap="round"
                  strokeDasharray="200 1000"
                  strokeDashoffset="0"
                  cx="100"
                  cy="100"
                  r="70"
                >
                  <animateTransform
                    type="rotate"
                    attributeName="transform"
                    calcMode="spline"
                    dur="2"
                    values="360;0"
                    keyTimes="0;1"
                    keySplines="0 0 1 1"
                    repeatCount="indefinite"
                  ></animateTransform>
                </circle>
                <circle
                  transform-origin="center"
                  fill="none"
                  opacity=".2"
                  stroke="#FF156D"
                  strokeWidth="15"
                  strokeLinecap="round"
                  cx="100"
                  cy="100"
                  r="70"
                ></circle>
              </svg>
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-[300px] max-h-[400px] overflow-auto hideScrollbar bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200/80 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] ring-1 ring-black/5 p-0"
        >
          <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200/80 w-full">
            <div className="px-4 py-2.5">
              <h3 className="text-base font-bold text-gray-900">Assign to Project</h3>
            </div>
          </div>
          {meeting.start && new Date(meeting.start).getTime() > Date.now() && (
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="record-toggle" className="text-sm">Record Meeting</Label>
                <Switch
                  id="record-toggle"
                  checked={isRecording}
                  onCheckedChange={handleToggleRecording}
                  disabled={isLoading}
                  className="data-[state=checked]:bg-[#FF4A8E] data-[state=checked]:border-[#FF4A8E]"
                />
              </div>
            </div>
          )}
          <Separator className="my-2" />
          {products.map((product, index) => (
            <div key={product.id}>
              <div 
                className="px-2 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                onClick={() => toggleProduct(product.id)}
              >
                <div className="flex items-center gap-2">
                  {getPlatformIcon(product.platform, 16, getStatusColor(product.status))}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-medium truncate max-w-[200px]">{truncateString(product.name, 24)}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{product.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {expandedProducts.has(product.id) ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </div>
              {expandedProducts.has(product.id) && (
                <div className="pl-4">
                  {product.projectsData.map((project) => (
                    <DropdownMenuItem
                      key={project.id}
                      onClick={() => handleAssignProject(project.id)}
                      className="cursor-pointer group"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <Check className="w-4 h-4 text-[#FF4A8E] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <ProjectSmallIcon />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate max-w-[220px]">{truncateString(project.name, 28)}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{project.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              )}
              {index < products.length - 1 && <Separator className="my-1" />}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function truncateString(str, maxLength) {
  if (!str) return ''
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str
}
